/**@jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Popover, Box } from '@mui/material';
import React, { useState } from 'react';
import { QSTypography } from '../../atoms';
import AccountManagerIcon from '../../../icons/AccountManagerIcon';
import HeadPhoneIcon from '../../../icons/HeadPhoneIcon';
import { openPopup } from '../../../utils/popup';
import { getUserChannelData } from '../../../../modules/login/v1/utility';
import { MAIN_IN_SUPPORT_NUMBER } from '../../../constants';
import { getSupportAccountManagerDetails } from '../../../requests/get-support-account-manager';
import useNotifications from '../../../hooks/use-notifications';
import Loader from '../../molecules/loader';

const ContactSupportOptions = ({ handleClose }: { handleClose: () => void }) => {
  const { addNotification } = useNotifications();
  const [isLoading, setIsLoading] = useState(false);
  const channelData = getUserChannelData();
  const hideDTDetails = !!channelData?.name;

  const handleContactAccountManager = async () => {
    let encodedMessage = encodeURIComponent('I would like to purchase add-ons and speak with my account manager.');
    try {
      setIsLoading(true);
      const supportAccountManagerDetails = await getSupportAccountManagerDetails();
      setIsLoading(false);
      if (supportAccountManagerDetails.accountManagerNumber) {
        openPopup(`https://wa.me/${supportAccountManagerDetails.accountManagerNumber}?text=${encodedMessage}`);
      } else {
        addNotification({
          message: 'No account manager found',
          type: 'error',
        });
      }
    } catch (error) {
      setIsLoading(false);
      addNotification({
        message: 'Error fetching account manager details',
        type: 'error',
      });
    }
  };

  const handleContactSupport = () => {
    let encodedMessage = '';
    if (hideDTDetails) {
      if (channelData?.name) {
        encodedMessage = encodeURIComponent(`I need support with an issue I'm facing in ${channelData?.name}`);
      } else {
        encodedMessage = encodeURIComponent(`I need support with an issue I'm facing`);
      }
      openPopup(`https://wa.me/${channelData.supportNumber}?text=${encodedMessage}`);
    } else {
      let encodedMessage = encodeURIComponent(`I need support with an issue I'm facing in Doubletick.`);
      openPopup(`https://wa.me/${MAIN_IN_SUPPORT_NUMBER}?text=${encodedMessage}`);
    }
    handleClose();
  };

  return (
    <Box
      css={css(
        `
        border-bottom: 1px solid #e0e1e4;
      `,
        hideDTDetails &&
          css`
            border-bottom: none;
          `
      )}
    >
      <Box
        component="button"
        onClick={handleContactSupport}
        css={css`
          width: 100%;
          display: flex;
          align-items: center;
          gap: 8px;
          height: 56px;
          padding: 12px 16px;
          background: transparent;
          border: none;
          cursor: pointer;
          &:hover {
            background-color: rgba(0, 0, 0, 0.04);
          }
        `}
      >
        <HeadPhoneIcon width={20} height={20} color="#7A7A7A" />
        <Box
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 2px;
          `}
        >
          <QSTypography
            css={css`
              font-size: 14px;
              font-weight: 500;
              color: #2d2d2d;
            `}
          >
            Contact support
          </QSTypography>
          <QSTypography
            css={css`
              font-size: 12px;
              font-weight: 400;
              color: #7a7a7a;
            `}
          >
            Technical issues and troubleshooting
          </QSTypography>
        </Box>
      </Box>

      {!hideDTDetails ? (
        <Box
          component="button"
          onClick={handleContactAccountManager}
          css={css`
            width: 100%;
            display: flex;
            align-items: center;
            height: 56px;
            width: 315px;
            padding: 12px 16px;
            background: transparent;
            border: none;
            cursor: pointer;
            &:hover {
              background-color: rgba(0, 0, 0, 0.04);
            }
          `}
        >
          <AccountManagerIcon size={20} color="#7A7A7A" />
          <Box
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 2px;
              margin-left: 8px;
            `}
          >
            <QSTypography
              css={css`
                font-size: 14px;
                font-weight: 500;
                color: #2d2d2d;
              `}
            >
              Contact your Account Manager
            </QSTypography>
            <QSTypography
              css={css`
                font-size: 12px;
                font-weight: 400;
                color: #7a7a7a;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                max-width: 100%;
                width: 100%;
              `}
            >
              Upgrade your plan or purchase add-ons
            </QSTypography>
          </Box>
          <div>{isLoading ? <Loader size={20} /> : null}</div>
        </Box>
      ) : null}
    </Box>
  );
};

export default ContactSupportOptions;
